import { useEffect, useState, useMemo } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useAuthStore } from '../store/auth'
import { useJobsStore, Scheduled } from '../store/jobs'
import { getCalendarMonth } from '../api/client'
import Layout from '../components/Layout'
import RefreshButton from '../components/RefreshButton'
import ViewModeToggle, { ViewMode } from '../components/ViewModeToggle'
import CalendarModal from '../components/CalendarModal'
import CopyButton from '../components/CopyButton'
import ErrorMessage from '../components/ErrorMessage'

interface CalendarData {
  year: number
  month: number
  days: Record<number, { count: number; items: Scheduled[] }>
}

export default function Calendar() {
  const [searchParams] = useSearchParams()
  const { nocodbScheduledViewUrl } = useAuthStore()
  const { loading, setLoading, error, setError } = useJobsStore()
  const [calendarData, setCalendarData] = useState<CalendarData | null>(null)
  const [currentDate, setCurrentDate] = useState(() => {
    const dateParam = searchParams.get('date')
    return dateParam ? new Date(dateParam) : new Date()
  })
  const [selectedDay, setSelectedDay] = useState<number | null>(null)
  const [inboxTracerFilter, setInboxTracerFilter] = useState('')
  const [viewMode, setViewMode] = useState<ViewMode>('monthly')
  const [selectedItem, setSelectedItem] = useState<Scheduled | null>(null)
  const [refreshKey, setRefreshKey] = useState(0)

  const year = currentDate.getFullYear()
  const month = currentDate.getMonth() + 1

  useEffect(() => {
    loadCalendar()
  }, [year, month, inboxTracerFilter, refreshKey])

  async function loadCalendar() {
    setLoading(true)
    setError(null)

    try {
      const data = await getCalendarMonth(
        year,
        month,
        inboxTracerFilter || undefined
      ) as CalendarData
      setCalendarData(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load calendar')
    } finally {
      setLoading(false)
    }
  }

  function previousPeriod() {
    if (viewMode === 'monthly') {
      setCurrentDate(new Date(year, month - 2, 1))
    } else if (viewMode === 'weekly') {
      setCurrentDate(new Date(currentDate.getTime() - 7 * 24 * 60 * 60 * 1000))
    } else {
      setCurrentDate(new Date(currentDate.getTime() - 24 * 60 * 60 * 1000))
    }
    setSelectedDay(null)
  }

  function nextPeriod() {
    if (viewMode === 'monthly') {
      setCurrentDate(new Date(year, month, 1))
    } else if (viewMode === 'weekly') {
      setCurrentDate(new Date(currentDate.getTime() + 7 * 24 * 60 * 60 * 1000))
    } else {
      setCurrentDate(new Date(currentDate.getTime() + 24 * 60 * 60 * 1000))
    }
    setSelectedDay(null)
  }

  function goToToday() {
    setCurrentDate(new Date())
    setSelectedDay(null)
  }

  function getDaysInMonth() {
    return new Date(year, month, 0).getDate()
  }

  function getFirstDayOfMonth() {
    return new Date(year, month - 1, 1).getDay()
  }

  // Get week dates for weekly view
  const weekDates = useMemo(() => {
    const dayOfWeek = currentDate.getDay()
    const startOfWeek = new Date(currentDate)
    startOfWeek.setDate(currentDate.getDate() - dayOfWeek)
    return Array.from({ length: 7 }, (_, i) => {
      const date = new Date(startOfWeek)
      date.setDate(startOfWeek.getDate() + i)
      return date
    })
  }, [currentDate])

  // Get items for a specific date
  function getItemsForDate(date: Date): Scheduled[] {
    if (!calendarData) return []
    const day = date.getDate()
    const itemMonth = date.getMonth() + 1
    const itemYear = date.getFullYear()
    if (itemMonth !== month || itemYear !== year) return []
    return calendarData.days[day]?.items || []
  }

  function getStatusColor(item: Scheduled) {
    if (item.proof_url) return 'bg-green-500' // Posted
    if (item.fired) return 'bg-yellow-500' // Sent
    return 'bg-blue-500' // Scheduled (pending)
  }

  function getStatusBadge(item: Scheduled) {
    if (item.proof_url) return { text: 'Posted', class: 'bg-green-100 text-green-800' }
    if (item.fired) return { text: 'Sent', class: 'bg-yellow-100 text-yellow-800' }
    return { text: 'Scheduled', class: 'bg-blue-100 text-blue-800' }
  }

  const daysInMonth = getDaysInMonth()
  const firstDay = getFirstDayOfMonth()
  const monthName = currentDate.toLocaleString('default', { month: 'long' })
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

  const volunteerBaseUrl = window.location.origin

  function handleItemUpdate(updated: Partial<Scheduled>) {
    if (!selectedItem || !calendarData) return
    const day = new Date(selectedItem.scheduled_datetime).getDate()
    const newData = { ...calendarData }
    const dayData = newData.days[day]
    if (dayData) {
      dayData.items = dayData.items.map((item) =>
        item.scheduled_id === selectedItem.scheduled_id
          ? { ...item, ...updated }
          : item
      )
    }
    setCalendarData(newData)
  }

  function handleItemDelete() {
    if (!selectedItem || !calendarData) return
    const day = new Date(selectedItem.scheduled_datetime).getDate()
    const newData = { ...calendarData }
    const dayData = newData.days[day]
    if (dayData) {
      dayData.items = dayData.items.filter(
        (item) => item.scheduled_id !== selectedItem.scheduled_id
      )
      dayData.count = dayData.items.length
    }
    setCalendarData(newData)
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Calendar</h1>
            <p className="text-gray-600">View your scheduled posts</p>
          </div>
          <div className="flex gap-3">
            {nocodbScheduledViewUrl && (
              <a
                href={nocodbScheduledViewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              >
                View Database
              </a>
            )}
            <RefreshButton
              onClick={() => setRefreshKey((k) => k + 1)}
              loading={loading}
              size="md"
            />
          </div>
        </div>

        {/* Controls */}
        <div className="flex flex-wrap gap-4 mb-6 items-center">
          <ViewModeToggle value={viewMode} onChange={setViewMode} />

          <button
            onClick={goToToday}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Today
          </button>

          <div className="flex-1" />

          <div className="max-w-xs w-full">
            <input
              type="text"
              value={inboxTracerFilter}
              onChange={(e) => setInboxTracerFilter(e.target.value)}
              placeholder="Filter by inbox tracer..."
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
        </div>

        {error && (
          <ErrorMessage
            message={error}
            className="mb-6"
            onDismiss={() => setError(null)}
          />
        )}

        {/* Status Legend */}
        <div className="flex gap-4 mb-4 text-xs">
          <div className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-full bg-blue-500" />
            <span className="text-gray-600">Pending</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-full bg-yellow-500" />
            <span className="text-gray-600">Sent</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-full bg-green-500" />
            <span className="text-gray-600">Posted</span>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {/* Navigation */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <button
              onClick={previousPeriod}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h2 className="text-lg font-semibold">
              {viewMode === 'daily'
                ? currentDate.toLocaleDateString('default', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
                : viewMode === 'weekly'
                ? `${weekDates[0].toLocaleDateString('default', { month: 'short', day: 'numeric' })} - ${weekDates[6].toLocaleDateString('default', { month: 'short', day: 'numeric', year: 'numeric' })}`
                : `${monthName} ${year}`}
            </h2>
            <button
              onClick={nextPeriod}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Calendar Views */}
          <div className="p-4">
            {viewMode === 'monthly' && (
              <>
                {/* Day Headers */}
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {dayNames.map((day) => (
                    <div
                      key={day}
                      className="text-center text-sm font-medium text-gray-500 py-2"
                    >
                      {day}
                    </div>
                  ))}
                </div>

                {/* Days */}
                <div className="grid grid-cols-7 gap-1">
                  {Array.from({ length: firstDay }).map((_, i) => (
                    <div key={`empty-${i}`} className="aspect-square" />
                  ))}

                  {Array.from({ length: daysInMonth }).map((_, i) => {
                    const day = i + 1
                    const dayData = calendarData?.days[day]
                    const isSelected = selectedDay === day
                    const isToday =
                      day === new Date().getDate() &&
                      month === new Date().getMonth() + 1 &&
                      year === new Date().getFullYear()

                    return (
                      <button
                        key={day}
                        onClick={() => setSelectedDay(isSelected ? null : day)}
                        className={`aspect-square p-1 rounded-lg border transition-colors ${
                          isSelected
                            ? 'border-indigo-500 bg-indigo-50'
                            : 'border-transparent hover:bg-gray-50'
                        } ${isToday ? 'ring-2 ring-indigo-300' : ''}`}
                      >
                        <div className="text-sm font-medium text-gray-700">{day}</div>
                        {dayData && dayData.count > 0 && (
                          <div className="mt-1 flex gap-0.5 justify-center flex-wrap">
                            {dayData.items.slice(0, 3).map((item) => (
                              <span
                                key={item.scheduled_id}
                                className={`w-2 h-2 rounded-full ${getStatusColor(item)}`}
                              />
                            ))}
                            {dayData.count > 3 && (
                              <span className="text-[8px] text-gray-400">
                                +{dayData.count - 3}
                              </span>
                            )}
                          </div>
                        )}
                      </button>
                    )
                  })}
                </div>
              </>
            )}

            {viewMode === 'weekly' && (
              <div className="grid grid-cols-7 gap-2">
                {weekDates.map((date) => {
                  const items = getItemsForDate(date)
                  const isToday = date.toDateString() === new Date().toDateString()
                  return (
                    <div
                      key={date.toISOString()}
                      className={`border rounded-lg p-2 min-h-[200px] ${
                        isToday ? 'border-indigo-300 bg-indigo-50/50' : 'border-gray-200'
                      }`}
                    >
                      <div className="text-center mb-2">
                        <div className="text-xs text-gray-500">{dayNames[date.getDay()]}</div>
                        <div className={`text-lg font-medium ${isToday ? 'text-indigo-600' : 'text-gray-700'}`}>
                          {date.getDate()}
                        </div>
                      </div>
                      <div className="space-y-1 max-h-[300px] overflow-auto">
                        {items.map((item) => (
                          <button
                            key={item.scheduled_id}
                            onClick={() => setSelectedItem(item)}
                            className="w-full text-left p-1.5 rounded bg-gray-50 hover:bg-gray-100 transition-colors"
                          >
                            <div className="flex items-center gap-1">
                              <span className={`w-2 h-2 rounded-full ${getStatusColor(item)}`} />
                              <span className="text-[10px] text-gray-600">
                                {new Date(item.scheduled_datetime).toLocaleTimeString([], {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                              </span>
                            </div>
                            <p className="text-[10px] text-gray-500 truncate mt-0.5">
                              {item.caption?.slice(0, 30)}...
                            </p>
                          </button>
                        ))}
                        {items.length === 0 && (
                          <p className="text-xs text-gray-400 text-center py-4">
                            No posts
                          </p>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}

            {viewMode === 'daily' && (
              <div className="space-y-3 max-h-[500px] overflow-auto">
                {getItemsForDate(currentDate).length > 0 ? (
                  getItemsForDate(currentDate).map((item) => (
                    <ScheduledItemCard
                      key={item.scheduled_id}
                      item={item}
                      volunteerBaseUrl={volunteerBaseUrl}
                      onEdit={() => setSelectedItem(item)}
                    />
                  ))
                ) : (
                  <p className="text-center text-gray-500 py-8">
                    No posts scheduled for this day.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Selected Day Details (Monthly view) */}
        {viewMode === 'monthly' && selectedDay && calendarData?.days[selectedDay] && (
          <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <h3 className="text-lg font-semibold mb-4">
              {monthName} {selectedDay}, {year}
            </h3>
            <div className="space-y-3">
              {calendarData.days[selectedDay].items.map((item) => (
                <ScheduledItemCard
                  key={item.scheduled_id}
                  item={item}
                  volunteerBaseUrl={volunteerBaseUrl}
                  onEdit={() => setSelectedItem(item)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {selectedItem && (
          <CalendarModal
            item={selectedItem}
            onClose={() => setSelectedItem(null)}
            onUpdate={handleItemUpdate}
            onDelete={handleItemDelete}
            volunteerBaseUrl={volunteerBaseUrl}
          />
        )}
      </div>
    </Layout>
  )
}

function ScheduledItemCard({
  item,
  volunteerBaseUrl,
  onEdit,
}: {
  item: Scheduled
  volunteerBaseUrl: string
  onEdit: () => void
}) {
  const volunteerLink = `${volunteerBaseUrl}/volunteer/${item.scheduled_id}`

  function getStatusBadge() {
    if (item.proof_url) return { text: 'Posted', class: 'bg-green-100 text-green-800' }
    if (item.fired) return { text: 'Sent', class: 'bg-yellow-100 text-yellow-800' }
    return { text: 'Scheduled', class: 'bg-blue-100 text-blue-800' }
  }

  const status = getStatusBadge()

  return (
    <div className="flex gap-4 p-4 border border-gray-200 rounded-lg hover:border-gray-300 transition-colors">
      {/* Thumbnail */}
      <div className="w-16 h-28 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
        {item.thumbnail_url || item.media_url ? (
          <img
            src={item.thumbnail_url || item.media_url}
            alt=""
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">
            {item.media_type}
          </div>
        )}
      </div>

      {/* Details */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm font-medium text-gray-900">
            {new Date(item.scheduled_datetime).toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${status.class}`}>
            {status.text}
          </span>
        </div>
        <p className="text-sm text-gray-600 line-clamp-2 mb-2">
          {item.caption}
        </p>
        <div className="flex items-center gap-2">
          <CopyButton text={volunteerLink} size="sm" label="Copy link" />
          {item.inbox_tracer && (
            <span className="text-xs text-gray-400">
              Tracer: {item.inbox_tracer}
            </span>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex-shrink-0 flex flex-col gap-2">
        <button
          onClick={onEdit}
          className="text-sm text-indigo-600 hover:text-indigo-700"
        >
          Edit
        </button>
        <Link
          to={`/volunteer/${item.scheduled_id}`}
          className="text-sm text-gray-600 hover:text-gray-700"
        >
          View
        </Link>
      </div>
    </div>
  )
}
